export const APPLICATION_SOURCE_DIMENSION_IDS = [
  "product-scope",
  "ui-structure",
  "visual-design",
  "client-state",
  "client-actionflow",
  "data-model",
  "datasource-interface",
  "external-business-rules",
  "auth-security",
  "error-recovery",
  "terminal-outcomes",
  "accessibility",
  "nonfunctional",
  "observability-audit",
  "deployment-operations",
] as const;

export type ApplicationSourceDimensionId =
  (typeof APPLICATION_SOURCE_DIMENSION_IDS)[number];

export const APPLICATION_SOURCE_RADAR_AXES = [
  {
    id: "scope-authority",
    label: "Scope",
    dimensionIds: ["product-scope"],
  },
  {
    id: "ui-design",
    label: "UI and design",
    dimensionIds: ["ui-structure", "visual-design"],
  },
  {
    id: "client-behavior",
    label: "Client behavior",
    dimensionIds: ["client-state", "client-actionflow"],
  },
  {
    id: "data-interface",
    label: "Data interface",
    dimensionIds: ["data-model", "datasource-interface"],
  },
  {
    id: "external-trust",
    label: "External trust",
    dimensionIds: [
      "external-business-rules",
      "auth-security",
      "error-recovery",
      "terminal-outcomes",
    ],
  },
  {
    id: "quality-operations",
    label: "Quality and operations",
    dimensionIds: [
      "accessibility",
      "nonfunctional",
      "observability-audit",
      "deployment-operations",
    ],
  },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  dimensionIds: readonly ApplicationSourceDimensionId[];
}>;

export type ApplicationSourceAuthorityState =
  | "observed"
  | "contracted"
  | "derived"
  | "candidate"
  | "mock-approved"
  | "external-out-of-scope"
  | "missing"
  | "conflict";

export type ApplicationGenerationLevel =
  | "visual-preview"
  | "client-prototype"
  | "contract-bound-client"
  | "integration-ready"
  | "production-ready";

export interface StudioSourceAssessmentDimension {
  dimensionId: ApplicationSourceDimensionId;
  responsibility: "cogo-owned" | "shared-interface" | "external-owned";
  authorityState: ApplicationSourceAuthorityState;
  score: number;
  verdict: string;
  requiredForTarget: boolean;
  acceptedForTarget: boolean;
  evidenceRefs: string[];
  blockerReason: string | null;
  clarificationRequestId: string | null;
}

export interface StudioSourceAssessmentRadarAxis {
  id: string;
  label: string;
  dimensionIds: ApplicationSourceDimensionId[];
  currentScore: number;
  targetScore: number;
  verdict: "sufficient" | "insufficient" | "not-required";
  blockerDimensionIds: ApplicationSourceDimensionId[];
}

export interface StudioSourceClarificationQuestion {
  id: string;
  dimensionId: ApplicationSourceDimensionId;
  question: string;
  decisionOptions: string[];
  blockedGenerationLevel: string;
  priority: number;
}

export interface StudioApplicationSourceAssessmentView {
  schemaVersion: "cogo.studio-application-source-assessment-view.v1";
  observedAt: string;
  assessmentRevision: string;
  projectId: string;
  subject: {
    schemaVersion: "cogo.ontology-subject-ref.v1";
    kind: "application-abox";
    subjectId: string;
  };
  sourceProfileId: string;
  primaryArchetype: string;
  systemCompositionProfiles: string[];
  requestedGenerationLevel: ApplicationGenerationLevel;
  maxSafeGenerationLevel: ApplicationGenerationLevel | null;
  readiness: "sufficient" | "insufficient";
  adapterBoundary: {
    productionClassifierAuthority: false;
  };
  radar: {
    chartType: "radar";
    aggregation: "minimum-required-dimension-score";
    scoreScale: { minimum: 0; maximum: 100 };
    scoreIsAuthority: false;
    series: Array<{
      id: "current-evidence" | "target-envelope";
      values: number[];
    }>;
    axes: StudioSourceAssessmentRadarAxis[];
  };
  dimensions: StudioSourceAssessmentDimension[];
  clarification: {
    ledgerCount: number;
    displayQuestions: StudioSourceClarificationQuestion[];
  };
  evidenceRefs: string[];
}

export interface StudioApplicationSourceSufficiencyGate {
  schemaVersion: "studio-application-source-sufficiency-gate.v1";
  status: "ready" | "candidate" | "blocked" | "not-required";
  required: boolean;
  requestedGenerationLevel: ApplicationGenerationLevel | null;
  maxSafeGenerationLevel: ApplicationGenerationLevel | null;
  assessmentRevision: string | null;
  sourceProfileId: string | null;
  primaryArchetype: string | null;
  blockerDimensionIds: ApplicationSourceDimensionId[];
  clarificationRequestIds: string[];
  evidenceRefs: string[];
  reasons: string[];
}

const DIMENSION_ID_SET = new Set<string>(APPLICATION_SOURCE_DIMENSION_IDS);
const AUTHORITY_STATES = new Set<string>([
  "observed",
  "contracted",
  "derived",
  "candidate",
  "mock-approved",
  "external-out-of-scope",
  "missing",
  "conflict",
]);
const GENERATION_LEVELS = new Set<string>([
  "visual-preview",
  "client-prototype",
  "contract-bound-client",
  "integration-ready",
  "production-ready",
]);

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function stringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(nonEmptyString);
}

function score(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 100
  );
}

function sameOrderedValues(
  left: readonly unknown[],
  right: readonly unknown[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function isDimension(value: unknown): value is StudioSourceAssessmentDimension {
  const item = record(value);
  return Boolean(
    item &&
    nonEmptyString(item.dimensionId) &&
    DIMENSION_ID_SET.has(item.dimensionId) &&
    ["cogo-owned", "shared-interface", "external-owned"].includes(
      String(item.responsibility),
    ) &&
    nonEmptyString(item.authorityState) &&
    AUTHORITY_STATES.has(item.authorityState) &&
    score(item.score) &&
    nonEmptyString(item.verdict) &&
    typeof item.requiredForTarget === "boolean" &&
    typeof item.acceptedForTarget === "boolean" &&
    stringArray(item.evidenceRefs) &&
    (item.blockerReason === null || typeof item.blockerReason === "string") &&
    (item.clarificationRequestId === null ||
      nonEmptyString(item.clarificationRequestId)),
  );
}

function isQuestion(
  value: unknown,
): value is StudioSourceClarificationQuestion {
  const item = record(value);
  return Boolean(
    item &&
    nonEmptyString(item.id) &&
    nonEmptyString(item.dimensionId) &&
    DIMENSION_ID_SET.has(item.dimensionId) &&
    nonEmptyString(item.question) &&
    stringArray(item.decisionOptions) &&
    item.decisionOptions.length > 0 &&
    nonEmptyString(item.blockedGenerationLevel) &&
    typeof item.priority === "number" &&
    Number.isFinite(item.priority),
  );
}

export function isStudioApplicationSourceAssessmentView(
  value: unknown,
): value is StudioApplicationSourceAssessmentView {
  const view = record(value);
  const subject = record(view?.subject);
  const adapterBoundary = record(view?.adapterBoundary);
  const radar = record(view?.radar);
  const scoreScale = record(radar?.scoreScale);
  const clarification = record(view?.clarification);
  if (
    !view ||
    view.schemaVersion !==
      "cogo.studio-application-source-assessment-view.v1" ||
    !nonEmptyString(view.observedAt) ||
    !nonEmptyString(view.assessmentRevision) ||
    !nonEmptyString(view.projectId) ||
    !subject ||
    subject.schemaVersion !== "cogo.ontology-subject-ref.v1" ||
    subject.kind !== "application-abox" ||
    !nonEmptyString(subject.subjectId) ||
    !nonEmptyString(view.sourceProfileId) ||
    !nonEmptyString(view.primaryArchetype) ||
    !stringArray(view.systemCompositionProfiles) ||
    !nonEmptyString(view.requestedGenerationLevel) ||
    !GENERATION_LEVELS.has(view.requestedGenerationLevel) ||
    !(
      view.maxSafeGenerationLevel === null ||
      (nonEmptyString(view.maxSafeGenerationLevel) &&
        GENERATION_LEVELS.has(view.maxSafeGenerationLevel))
    ) ||
    !["sufficient", "insufficient"].includes(String(view.readiness)) ||
    adapterBoundary?.productionClassifierAuthority !== false ||
    radar?.chartType !== "radar" ||
    radar.aggregation !== "minimum-required-dimension-score" ||
    radar.scoreIsAuthority !== false ||
    scoreScale?.minimum !== 0 ||
    scoreScale.maximum !== 100 ||
    !Array.isArray(radar.axes) ||
    radar.axes.length !== APPLICATION_SOURCE_RADAR_AXES.length ||
    !Array.isArray(radar.series) ||
    radar.series.length !== 2 ||
    !Array.isArray(view.dimensions) ||
    view.dimensions.length !== APPLICATION_SOURCE_DIMENSION_IDS.length ||
    !view.dimensions.every(isDimension) ||
    !clarification ||
    typeof clarification.ledgerCount !== "number" ||
    clarification.ledgerCount < 0 ||
    !Array.isArray(clarification.displayQuestions) ||
    clarification.displayQuestions.length > 3 ||
    !clarification.displayQuestions.every(isQuestion) ||
    !stringArray(view.evidenceRefs)
  ) {
    return false;
  }

  const dimensionById = new Map(
    view.dimensions.map((item) => [item.dimensionId, item]),
  );
  if (dimensionById.size !== APPLICATION_SOURCE_DIMENSION_IDS.length)
    return false;
  const partitionedDimensions: string[] = [];
  for (const [index, expected] of APPLICATION_SOURCE_RADAR_AXES.entries()) {
    const axis = record(radar.axes[index]);
    if (
      !axis ||
      axis.id !== expected.id ||
      axis.label !== expected.label ||
      !Array.isArray(axis.dimensionIds) ||
      !sameOrderedValues(axis.dimensionIds, expected.dimensionIds) ||
      !score(axis.currentScore) ||
      !score(axis.targetScore) ||
      !["sufficient", "insufficient", "not-required"].includes(
        String(axis.verdict),
      ) ||
      !Array.isArray(axis.blockerDimensionIds) ||
      !axis.blockerDimensionIds.every(
        (dimensionId) =>
          typeof dimensionId === "string" &&
          (expected.dimensionIds as readonly string[]).includes(dimensionId),
      )
    ) {
      return false;
    }
    partitionedDimensions.push(...(axis.dimensionIds as string[]));
    const dimensions = expected.dimensionIds.map((dimensionId) =>
      dimensionById.get(dimensionId),
    );
    if (dimensions.some((dimension) => !dimension)) return false;
    const requiredDimensions = dimensions.filter(
      (dimension) => dimension?.requiredForTarget,
    );
    if (requiredDimensions.length === 0) {
      if (axis.verdict !== "not-required" || axis.targetScore !== 0)
        return false;
    } else {
      const minimumRequiredScore = Math.min(
        ...requiredDimensions.map((dimension) => dimension!.score),
      );
      if (axis.currentScore !== minimumRequiredScore) return false;
    }
  }
  if (
    !sameOrderedValues(partitionedDimensions, APPLICATION_SOURCE_DIMENSION_IDS)
  )
    return false;

  const currentSeries = record(radar.series[0]);
  const targetSeries = record(radar.series[1]);
  const axes = radar.axes as unknown as StudioSourceAssessmentRadarAxis[];
  if (
    currentSeries?.id !== "current-evidence" ||
    !Array.isArray(currentSeries.values) ||
    !sameOrderedValues(
      currentSeries.values,
      axes.map((axis) => axis.currentScore),
    ) ||
    targetSeries?.id !== "target-envelope" ||
    !Array.isArray(targetSeries.values) ||
    !sameOrderedValues(
      targetSeries.values,
      axes.map((axis) => axis.targetScore),
    )
  ) {
    return false;
  }

  const questionIds = new Set(
    (clarification.displayQuestions as StudioSourceClarificationQuestion[]).map(
      (question) => question.id,
    ),
  );
  const ledgerCount = clarification.ledgerCount as number;
  const displayQuestionCount = (
    clarification.displayQuestions as StudioSourceClarificationQuestion[]
  ).length;
  return view.dimensions.every(
    (dimension) =>
      !dimension.requiredForTarget ||
      dimension.acceptedForTarget ||
      Boolean(
        dimension.clarificationRequestId &&
        questionIds.has(dimension.clarificationRequestId),
      ) ||
      ledgerCount > displayQuestionCount,
  );
}

export function readProjectApplicationSourceAssessment(
  project: unknown,
): StudioApplicationSourceAssessmentView | null {
  const root = record(project);
  const manifest = record(root?.manifest);
  const metadata = record(root?.metadata);
  const settings = record(root?.settings);
  const applicationMetadata = record(settings?.cogoApplicationMetadata);
  const candidates = [
    applicationMetadata?.applicationSourceAssessment,
    metadata?.applicationSourceAssessment,
    manifest?.applicationSourceAssessment,
    root?.applicationSourceAssessment,
  ];
  return candidates.find(isStudioApplicationSourceAssessmentView) ?? null;
}

export function evaluateApplicationSourceSufficiencyGate(
  project: unknown,
): StudioApplicationSourceSufficiencyGate {
  const assessment = readProjectApplicationSourceAssessment(project);
  if (!assessment) {
    const root = record(project);
    const claimsSourceDerivedGeneration = Boolean(
      record(root?.manifest)?.sourceDerivedGeneration === true ||
      record(root?.metadata)?.sourceDerivedGeneration === true,
    );
    return {
      schemaVersion: "studio-application-source-sufficiency-gate.v1",
      status: claimsSourceDerivedGeneration ? "blocked" : "not-required",
      required: claimsSourceDerivedGeneration,
      requestedGenerationLevel: null,
      maxSafeGenerationLevel: null,
      assessmentRevision: null,
      sourceProfileId: null,
      primaryArchetype: null,
      blockerDimensionIds: [],
      clarificationRequestIds: [],
      evidenceRefs: [],
      reasons: claimsSourceDerivedGeneration
        ? [
            "Source-derived generation requires a server-normalized application source assessment.",
          ]
        : [],
    };
  }

  const blockers = assessment.dimensions.filter(
    (dimension) => dimension.requiredForTarget && !dimension.acceptedForTarget,
  );
  const clarificationRequestIds = blockers
    .map((dimension) => dimension.clarificationRequestId)
    .filter((id): id is string => Boolean(id));
  const ready = assessment.readiness === "sufficient" && blockers.length === 0;
  return {
    schemaVersion: "studio-application-source-sufficiency-gate.v1",
    status: ready
      ? "ready"
      : assessment.readiness === "insufficient"
        ? "blocked"
        : "candidate",
    required: true,
    requestedGenerationLevel: assessment.requestedGenerationLevel,
    maxSafeGenerationLevel: assessment.maxSafeGenerationLevel,
    assessmentRevision: assessment.assessmentRevision,
    sourceProfileId: assessment.sourceProfileId,
    primaryArchetype: assessment.primaryArchetype,
    blockerDimensionIds: blockers.map((dimension) => dimension.dimensionId),
    clarificationRequestIds,
    evidenceRefs: assessment.evidenceRefs,
    reasons: ready
      ? []
      : blockers.map(
          (dimension) =>
            dimension.blockerReason ??
            `${dimension.dimensionId} is not accepted for ${assessment.requestedGenerationLevel}.`,
        ),
  };
}
